import {
  assertSessionGenerationCurrent, captureSessionGeneration, createIdempotencyKey,
  getAccessToken, getStoredContext, getStoredPermissions, request
} from "../api/client";
import { LEGACY_OFFLINE_DATABASE_NAME, OFFLINE_QUEUE_EVENT } from "../brand";

export interface OfflineContext { tenantId: string; branchId: string; userId: string }
interface OfflineSession {
  context: OfflineContext;
  generation: number;
  token: string;
  canReview: boolean;
  canReplay: boolean;
}

class OfflineQueueError extends Error {
  readonly code: string;
  constructor(code: string, message = code) {
    super(message);
    this.code = code;
    this.name = "OfflineQueueError";
  }
}

const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isContextId = (value: unknown): value is string =>
  typeof value === "string" && guid.test(value) && value !== "00000000-0000-0000-0000-000000000000";

function sameOfflineContext(left: OfflineContext, right: OfflineContext): boolean {
  return left.tenantId.toLowerCase() === right.tenantId.toLowerCase()
    && left.branchId.toLowerCase() === right.branchId.toLowerCase()
    && left.userId.toLowerCase() === right.userId.toLowerCase();
}

// These claims are a client identity snapshot, NOT authorization. The existing
// authenticated endpoint still validates the token, permissions and order scope.
function captureOfflineSession(): OfflineSession {
  const generation = captureSessionGeneration();
  const token = getAccessToken();
  try {
    const stored = getStoredContext();
    if (!token || !stored) throw new Error();
    const parts = token.split(".");
    if (parts.length !== 3) throw new Error();
    const encoded = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=")));
    if (claims.scope !== "tenant" || !isContextId(claims.sub)
      || !isContextId(claims.tenant_id) || !isContextId(claims.branch_id)
      || typeof claims.exp !== "number" || !Number.isFinite(claims.exp)
      || claims.exp * 1000 <= Date.now()
      || claims.tenant_id.toLowerCase() !== stored.tenantId.toLowerCase()
      || claims.branch_id.toLowerCase() !== stored.branchId.toLowerCase()) throw new Error();
    const permissions = getStoredPermissions().map(value => value.toLowerCase());
    const canReplay = permissions.includes("orders.update");
    const canReview = canReplay && ["owner", "manager"].includes(stored.roleName.toLowerCase());
    assertSessionGenerationCurrent(generation);
    return {
      context: { tenantId: claims.tenant_id.toLowerCase(), branchId: claims.branch_id.toLowerCase(), userId: claims.sub.toLowerCase() },
      token, generation, canReview, canReplay
    };
  } catch {
    throw new OfflineQueueError("OFFLINE_SESSION_REQUIRED");
  }
}

function assertOfflineSessionCurrent(session: OfflineSession): void {
  assertSessionGenerationCurrent(session.generation);
  const current = captureOfflineSession();
  if (!sameOfflineContext(session.context, current.context) || session.token !== current.token)
    throw new OfflineQueueError("OFFLINE_SESSION_CHANGED");
}

export interface OfflineMutation {
  id: string;
  path: string;
  method: "POST" | "PUT" | "DELETE";
  body: unknown;
  idempotencyKey: string;
  createdAt: string;
  attempts: number;
  lastError: string | null;
  financial: boolean;
  schemaVersion?: 2;
  context?: OfflineContext;
}

type MutationInput = Pick<OfflineMutation, "id" | "path" | "method" | "body" | "idempotencyKey" | "financial">;
export interface OfflineQueueSummary { pendingCount: number; requiresReview: boolean }
export interface OfflineReplayResult { completed: number; failed: number; errorCode: string | null }

const DATABASE_NAME = LEGACY_OFFLINE_DATABASE_NAME;
const STORE_NAME = "mutations";
// Additive record fields only. Unknown/unscoped rows are never rewritten.
const DATABASE_VERSION = 1;
const LOCK_NAME = DATABASE_NAME + ":" + STORE_NAME + ":replay-v2";
const changed = () => window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_EVENT));
const financialQueueError = () => new OfflineQueueError(
  "OFFLINE_OPERATION_UNSUPPORTED", "Financial operations cannot be queued offline."
);
const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function storedContext(value: unknown): OfflineContext | null {
  if (!record(value) || !record(value.context)) return null;
  const { tenantId, branchId, userId } = value.context;
  return isContextId(tenantId) && isContextId(branchId) && isContextId(userId)
    ? { tenantId, branchId, userId } : null;
}

function supportedMutation(value: unknown): value is MutationInput {
  if (!record(value) || value.financial !== false || typeof value.id !== "string"
    || !value.id.trim() || value.id.length > 160 || value.id !== value.idempotencyKey
    || typeof value.path !== "string" || !record(value.body)
    || !Number.isSafeInteger(value.body.expectedVersion) || Number(value.body.expectedVersion) < 0) return false;
  const path = /^\/api\/v1\/cashier\/orders\/([^/]+)\/(context|hold)$/.exec(value.path);
  if (!path || !isContextId(path[1])) return false;
  return path[2] === "hold"
    ? value.method === "POST" && typeof value.body.reason === "string" && Boolean(value.body.reason.trim())
    : value.method === "PUT" && ["WalkIn", "DineIn", "Takeaway", "Delivery"].includes(String(value.body.serviceType));
}

function replayable(value: unknown, session: OfflineSession): value is OfflineMutation & { context: OfflineContext } {
  const context = storedContext(value);
  return session.canReplay && context !== null && sameOfflineContext(context, session.context)
    && record(value) && value.schemaVersion === 2
    && typeof value.createdAt === "string" && Number.isFinite(Date.parse(value.createdAt))
    && Number.isSafeInteger(value.attempts) && Number(value.attempts) >= 0 && supportedMutation(value);
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const operation = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    let blocked = false;
    operation.onupgradeneeded = () => {
      if (!operation.result.objectStoreNames.contains(STORE_NAME))
        operation.result.createObjectStore(STORE_NAME, { keyPath: "id" });
    };
    operation.onblocked = () => { blocked = true; reject(new OfflineQueueError("OFFLINE_STORAGE_BLOCKED")); };
    operation.onerror = () => reject(new OfflineQueueError("OFFLINE_STORAGE_FAILED"));
    operation.onsuccess = () => {
      if (blocked) { operation.result.close(); return; }
      operation.result.onversionchange = () => operation.result.close();
      resolve(operation.result);
    };
  });
}

// A request's success is NOT a commit. Settle at the transaction boundary,
// including abort, request errors, and synchronous exceptions.
async function withStore<T>(mode: IDBTransactionMode,
  run: (store: IDBObjectStore, done: (value: T) => void, fail: (error: unknown) => void) => void
): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    let transaction: IDBTransaction;
    let result: T;
    let hasResult = false;
    let failure: unknown;
    const fail = (error: unknown) => {
      failure = error;
      try { transaction.abort(); } catch { database.close(); reject(error); }
    };
    try {
      transaction = database.transaction(STORE_NAME, mode);
      transaction.oncomplete = () => {
        database.close();
        if (hasResult) resolve(result);
        else reject(new OfflineQueueError("OFFLINE_STORAGE_FAILED"));
      };
      transaction.onerror = () => { failure ??= new OfflineQueueError("OFFLINE_STORAGE_FAILED"); };
      transaction.onabort = () => {
        database.close();
        reject(failure ?? new OfflineQueueError("OFFLINE_STORAGE_FAILED"));
      };
      run(transaction.objectStore(STORE_NAME), value => { result = value; hasResult = true; }, fail);
    } catch (error) {
      // If run enqueued writes before throwing, abort them instead of allowing
      // an unacknowledged transaction to commit.
      fail(error instanceof OfflineQueueError ? error : new OfflineQueueError("OFFLINE_STORAGE_FAILED"));
    }
  });
}

function onResult<T>(operation: IDBRequest<T>, action: (value: T) => void, fail: (error: unknown) => void): void {
  operation.onsuccess = () => { try { action(operation.result); } catch (error) { fail(error); } };
  // Request errors bubble to the transaction; never prevent its abort.
}

function readItems(): Promise<unknown[]> {
  return withStore("readonly", (store, done, fail) => onResult(store.getAll(), done, fail));
}

export async function getOfflineQueueSummary(): Promise<OfflineQueueSummary> {
  const session = captureOfflineSession();
  const items = await readItems();
  assertOfflineSessionCurrent(session);
  let pendingCount = 0;
  let requiresReview = false;
  for (const item of items) {
    if (replayable(item, session)) pendingCount++;
    else if (session.canReview) {
      const context = storedContext(item);
      if (!context || sameOfflineContext(context, session.context)) requiresReview = true;
    }
  }
  return { pendingCount, requiresReview };
}

export async function listOfflineMutations(): Promise<OfflineMutation[]> {
  const session = captureOfflineSession();
  const items = await readItems();
  assertOfflineSessionCurrent(session);
  return items.filter(item => replayable(item, session)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function countOfflineMutations(): Promise<number> {
  return (await getOfflineQueueSummary()).pendingCount;
}

async function persistMutation(mutation: MutationInput, session: OfflineSession): Promise<OfflineMutation> {
  if (mutation.financial) throw financialQueueError();
  if (!session.canReplay) throw new OfflineQueueError("OFFLINE_PERMISSION_DENIED");
  if (!supportedMutation(mutation)) throw new OfflineQueueError("OFFLINE_OPERATION_UNSUPPORTED");
  const item: OfflineMutation = {
    id: mutation.id, path: mutation.path, method: mutation.method,
    idempotencyKey: mutation.idempotencyKey, financial: mutation.financial,
    body: structuredClone(mutation.body), schemaVersion: 2,
    context: { ...session.context }, createdAt: new Date().toISOString(), attempts: 0, lastError: null
  };
  const saved = await withStore<OfflineMutation>("readwrite", (store, done, fail) => {
    onResult(store.get(item.id), existing => {
      if (existing !== undefined) {
        // Never overwrite an old/foreign row, change its body or reset history.
        if (!replayable(existing, session) || existing.path !== item.path || existing.method !== item.method
          || JSON.stringify(existing.body) !== JSON.stringify(item.body))
          throw new OfflineQueueError("OFFLINE_OPERATION_CONFLICT");
        done(existing);
      } else onResult(store.add(item), () => done(item), fail);
    }, fail);
  });
  changed();
  // Session changes do not delete the item saved for the original user.
  assertOfflineSessionCurrent(session);
  return saved;
}

export function enqueueOfflineMutation(mutation: MutationInput): Promise<OfflineMutation> {
  return persistMutation(mutation, captureOfflineSession());
}

// The read/compare/write is atomic. A late failure cannot recreate a row that
// has already been removed; a changed row is preserved rather than overwritten.
async function settleMutation(item: OfflineMutation, errorCode: string | null): Promise<void> {
  await withStore<void>("readwrite", (store, done, fail) => {
    onResult(store.get(item.id), current => {
      if (current === undefined) { done(); return; }
      if (JSON.stringify(current) !== JSON.stringify(item)) throw new OfflineQueueError("OFFLINE_OPERATION_CONFLICT");
      if (errorCode === null) onResult(store.delete(item.id), () => done(), fail);
      else onResult(store.put({ ...item, attempts: item.attempts + 1, lastError: errorCode }), () => done(), fail);
    }, fail);
  });
  changed();
}

export function offlineQueueErrorCode(error: unknown): string {
  if (error instanceof OfflineQueueError) return error.code;
  if (record(error) && ["SESSION_CONTEXT_CHANGED", "SESSION_CONTEXT_STALE_RESPONSE"].includes(String(error.code)))
    return "OFFLINE_SESSION_CHANGED";
  const status = record(error) ? Number(error.status) : 0;
  if (status === 401) return "OFFLINE_SESSION_REQUIRED";
  if (status === 403) return "OFFLINE_PERMISSION_DENIED";
  if (status === 409) return "OFFLINE_CONFLICT";
  if (status === 404 || status === 422) return "OFFLINE_REVIEW_REQUIRED";
  return "OFFLINE_RETRY_REQUIRED";
}

export async function replayOfflineMutations(): Promise<OfflineReplayResult> {
  const result: OfflineReplayResult = { completed: 0, failed: 0, errorCode: null };
  if (!navigator.onLine) return result;
  const session = captureOfflineSession();
  if (!session.canReplay) throw new OfflineQueueError("OFFLINE_PERMISSION_DENIED");
  if (!navigator.locks?.request) throw new OfflineQueueError("OFFLINE_LOCK_UNAVAILABLE");
  // Non-blocking acquisition avoids queued background retries from other tabs.
  return navigator.locks.request(LOCK_NAME, { mode: "exclusive", ifAvailable: true }, async lock => {
    if (!lock) return { ...result, errorCode: "OFFLINE_SYNC_BUSY" };
    assertOfflineSessionCurrent(session);
    const items = await readItems();
    assertOfflineSessionCurrent(session);
    const eligible = items.filter(item => replayable(item, session)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const item of eligible) {
      assertOfflineSessionCurrent(session);
      if (!navigator.onLine) break;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30_000);
      try {
        await request<unknown>(item.path, {
          method: item.method, headers: { "Idempotency-Key": item.idempotencyKey },
          body: JSON.stringify(item.body), signal: controller.signal
        }, session.token);
        assertOfflineSessionCurrent(session);
      } catch (error) {
        result.failed++;
        result.errorCode = offlineQueueErrorCode(error);
        // The server may already have committed. A stale session stops the
        // batch and preserves the original item/key without a foreign update.
        assertOfflineSessionCurrent(session);
        await settleMutation(item, result.errorCode);
        break;
      } finally {
        clearTimeout(timeout);
      }
      try {
        await settleMutation(item, null);
        result.completed++;
      } catch {
        // Never rewrite a server success as a network failure.
        result.failed++;
        result.errorCode = "OFFLINE_ACK_PENDING";
        break;
      }
    }
    return result;
  });
}

export async function executeOrQueue<T>({ path, method, body,
  operationId = createIdempotencyKey(), financial = false
}: {
  path: string; method: "POST" | "PUT" | "DELETE"; body: unknown;
  operationId?: string; financial?: boolean;
}): Promise<{ queued: false; data: T; operationId: string } | { queued: true; data: null; operationId: string }> {
  // Snapshot before HTTP: do not adopt the new account on network failure.
  const session = captureOfflineSession();
  const mutation = { id: operationId, path, method, body: structuredClone(body), idempotencyKey: operationId, financial };
  const requestBody = body == null ? undefined : JSON.stringify(mutation.body);
  if (financial && !navigator.onLine) throw financialQueueError();
  if (navigator.onLine) {
    try {
      const data = await request<T>(path, { method, headers: { "Idempotency-Key": operationId },
        body: requestBody }, session.token);
      assertOfflineSessionCurrent(session);
      return { queued: false, data, operationId };
    } catch (error) {
      // Business/HTTP errors containing 'network' must not enter the queue.
      if (!(error instanceof TypeError) || financial) throw error;
    }
  }
  await persistMutation(mutation, session);
  return { queued: true, data: null, operationId };
}
